import * as THREE from 'three';

export class Camera {
  constructor(renderer) {
    this.renderer = renderer;
    this.target = null; // The object to follow (player mesh)
    this.targetPosition = new THREE.Vector3();
    this.currentPosition = new THREE.Vector3();
    this.currentLookAt = new THREE.Vector3();

    // Camera orbit parameters (spherical coordinates)
    this.distance = 45; // Distance from player
    this.minDistance = 15;
    this.maxDistance = 100;
    this.azimuth = 0; // Horizontal rotation (radians)
    this.polar = Math.PI / 4; // Vertical angle (radians) - 45 degrees from horizontal
    this.minPolar = 0.2; // Minimum vertical angle (prevent going below ground)
    this.maxPolar = Math.PI / 2.2; // Maximum vertical angle (prevent going too high)

    this.lookAtOffset = new THREE.Vector3(0, 2, 0); // Look slightly above player

    // Smoothing factor for camera movement
    this.smoothing = 0.08;

    // Brief shake when the player is hit (decays to zero)
    this.shakeAmount = 0;

    // Mouse control state
    this.isDragging = false;
    this.lastMouseX = 0;
    this.lastMouseY = 0;
    this.rotationSpeed = 0.005;
    this.zoomSpeed = 0.1;

    // Callback for when camera rotation changes (for updating movement direction)
    this.onRotationChange = null;

    // Spectator free camera mode
    this.isSpectatorMode = false;
    this.spectatorTarget = new THREE.Vector3(0, 0, 0); // Center of arena
    this.spectatorMoveSpeed = 80; // Units per second
    this.spectatorKeys = { w: false, a: false, s: false, d: false };

    this.create();
    this.setupControls();
  }

  create() {
    // Create perspective camera
    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.5,
      2000
    );

    // Initial position (will be updated when following player)
    this.camera.position.set(0, 80, 80);
    this.camera.lookAt(0, 0, 0);

    this.currentPosition.copy(this.camera.position);
    this.currentLookAt.set(0, 0, 0);
  }

  setupControls() {
    const canvas = this.renderer.domElement;

    // Right-click or middle-click drag to rotate
    canvas.addEventListener('mousedown', (e) => {
      if (e.button === 2 || e.button === 1) { // Right or middle click
        this.isDragging = true;
        this.lastMouseX = e.clientX;
        this.lastMouseY = e.clientY;
        e.preventDefault();
      }
    });

    window.addEventListener('mouseup', (e) => {
      if (e.button === 2 || e.button === 1) {
        this.isDragging = false;
      }
    });

    window.addEventListener('mousemove', (e) => {
      if (this.isDragging) {
        const deltaX = e.clientX - this.lastMouseX;
        const deltaY = e.clientY - this.lastMouseY;

        // Update azimuth (horizontal rotation)
        this.azimuth -= deltaX * this.rotationSpeed;

        // Update polar (vertical rotation)
        this.polar += deltaY * this.rotationSpeed;
        this.polar = Math.max(this.minPolar, Math.min(this.maxPolar, this.polar));

        this.lastMouseX = e.clientX;
        this.lastMouseY = e.clientY;

        // Notify listeners that rotation changed (for camera-relative movement)
        if (this.onRotationChange) {
          this.onRotationChange();
        }
      }
    });

    // Mouse wheel to zoom
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      const zoomDelta = e.deltaY * this.zoomSpeed;
      this.distance += zoomDelta;
      this.distance = Math.max(this.minDistance, Math.min(this.maxDistance, this.distance));
    }, { passive: false });

    // Prevent context menu on right-click
    canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // Keyboard controls for spectator free camera
    window.addEventListener('keydown', (e) => {
      if (!this.isSpectatorMode) return;
      const key = e.key.toLowerCase();
      if (key in this.spectatorKeys) {
        this.spectatorKeys[key] = true;
      }
    });

    window.addEventListener('keyup', (e) => {
      const key = e.key.toLowerCase();
      if (key in this.spectatorKeys) {
        this.spectatorKeys[key] = false;
      }
    });
  }

  // Calculate camera offset from spherical coordinates
  calculateOffset() {
    const x = this.distance * Math.sin(this.polar) * Math.sin(this.azimuth);
    const y = this.distance * Math.cos(this.polar);
    const z = this.distance * Math.sin(this.polar) * Math.cos(this.azimuth);
    return new THREE.Vector3(x, y, z);
  }

  // Set the target to follow (player mesh)
  setTarget(targetMesh) {
    this.target = targetMesh;
    if (targetMesh) {
      // Initialize camera position behind player
      const offset = this.calculateOffset();
      this.targetPosition.copy(targetMesh.position).add(offset);
      this.currentPosition.copy(this.targetPosition);
      this.camera.position.copy(this.currentPosition);
    }
  }

  update(deltaTime = 1/60) {
    // Handle spectator free camera mode
    if (this.isSpectatorMode) {
      this.updateSpectatorCamera(deltaTime);
      return;
    }

    if (this.target && this.target.position) {
      // Calculate offset based on current spherical coordinates
      const offset = this.calculateOffset();

      // Calculate desired camera position
      this.targetPosition.copy(this.target.position).add(offset);

      // Smoothly interpolate camera position
      this.currentPosition.lerp(this.targetPosition, this.smoothing);
      this.camera.position.copy(this.currentPosition);

      // Calculate look-at target (player position + offset)
      const lookAtTarget = this.target.position.clone().add(this.lookAtOffset);

      // Smoothly interpolate look-at
      this.currentLookAt.lerp(lookAtTarget, this.smoothing);
      this.camera.lookAt(this.currentLookAt);

      if (this.shakeAmount > 0) {
        const s = this.shakeAmount * 0.6;
        this.camera.position.x += (Math.random() - 0.5) * s;
        this.camera.position.y += (Math.random() - 0.5) * s;
        this.camera.position.z += (Math.random() - 0.5) * s;
        this.shakeAmount = Math.max(0, this.shakeAmount - deltaTime * 4);
      }
    }
  }

  // Jolt the camera; amount 0..1
  shake(amount) {
    this.shakeAmount = Math.min(1, this.shakeAmount + amount);
  }

  updateSpectatorCamera(deltaTime) {
    // Move spectator target based on WASD input (camera-relative)
    const forward = this.getForwardDirection();
    const right = this.getRightDirection();

    let moveX = 0;
    let moveZ = 0;

    if (this.spectatorKeys.w) {
      moveX += forward.x;
      moveZ += forward.z;
    }
    if (this.spectatorKeys.s) {
      moveX -= forward.x;
      moveZ -= forward.z;
    }
    if (this.spectatorKeys.d) {
      moveX += right.x;
      moveZ += right.z;
    }
    if (this.spectatorKeys.a) {
      moveX -= right.x;
      moveZ -= right.z;
    }

    // Normalize and apply speed
    const length = Math.sqrt(moveX * moveX + moveZ * moveZ);
    if (length > 0) {
      const speed = this.spectatorMoveSpeed * deltaTime;
      this.spectatorTarget.x += (moveX / length) * speed;
      this.spectatorTarget.z += (moveZ / length) * speed;

      // Clamp to arena bounds (roughly -150 to 150)
      this.spectatorTarget.x = Math.max(-150, Math.min(150, this.spectatorTarget.x));
      this.spectatorTarget.z = Math.max(-150, Math.min(150, this.spectatorTarget.z));
    }

    // Calculate offset based on current spherical coordinates
    const offset = this.calculateOffset();

    // Calculate desired camera position
    this.targetPosition.copy(this.spectatorTarget).add(offset);

    // Smoothly interpolate camera position
    this.currentPosition.lerp(this.targetPosition, this.smoothing);
    this.camera.position.copy(this.currentPosition);

    // Calculate look-at target
    const lookAtTarget = this.spectatorTarget.clone().add(this.lookAtOffset);

    // Smoothly interpolate look-at
    this.currentLookAt.lerp(lookAtTarget, this.smoothing);
    this.camera.lookAt(this.currentLookAt);
  }

  handleResize() {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }

  getCamera() {
    return this.camera;
  }

  // Get the current look-at position (for raycasting ground plane)
  getLookAtPosition() {
    return this.currentLookAt.clone();
  }

  // Get the camera's forward direction on the XZ plane (normalized)
  // Used for camera-relative movement controls
  getForwardDirection() {
    return {
      x: -Math.sin(this.azimuth),
      z: -Math.cos(this.azimuth)
    };
  }

  // Get the camera's right direction on the XZ plane (normalized)
  // Used for camera-relative movement controls
  getRightDirection() {
    return {
      x: Math.cos(this.azimuth),
      z: -Math.sin(this.azimuth)
    };
  }

  // Set callback for when camera rotation changes
  setRotationChangeCallback(callback) {
    this.onRotationChange = callback;
  }

  // Enable or disable spectator free camera mode
  setSpectatorMode(enabled) {
    this.isSpectatorMode = enabled;
    if (enabled) {
      // Reset spectator target to center of arena
      this.spectatorTarget.set(0, 0, 0);
      // Zoom out a bit for better overview
      this.distance = Math.max(this.distance, 70);
      // Clear any key states
      this.spectatorKeys = { w: false, a: false, s: false, d: false };
    }
  }
}
